import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Switch,
  Alert,
  AlertTitle,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Grid,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  NetworkCheck as TestIcon,
  Refresh as RefreshIcon,
  Public as FeedIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { formatRelativeTime } from '../../utils/dateUtils';

const serverMessage = (err, fallback) => err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback;

const FORMAT_LABELS = {
  auto: 'Auto-detect',
  text: 'Plain text list',
  csv: 'CSV',
  stix: 'STIX 2.x bundle',
  taxii: 'TAXII 2.1 collection',
};

const AUTH_LABELS = {
  none: 'None',
  basic: 'Basic (username + password)',
  bearer: 'Bearer token',
  header: 'Custom header',
};

const truncate = (value, max = 46) => {
  const s = String(value || '');
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
};

const emptyForm = {
  name: '',
  url: '',
  format: 'auto',
  enabled: true,
  intervalHours: 6,
  maxIndicators: 5000,
  authType: 'none',
  username: '',
  headerName: '',
  apiKey: '',
  apiToken: '',
  clientSecret: '',
  confidence: 'Medium',
  severity: 'Medium',
  tlp: 'AMBER',
  tags: [],
  reactivate: false,
  description: '',
  cleared: {},
};

const formFromFeed = (f) => ({
  ...emptyForm,
  name: f.name || '',
  url: f.url || '',
  format: f.format || 'auto',
  enabled: f.enabled !== false,
  intervalHours: f.intervalHours || 6,
  maxIndicators: f.maxIndicators || 5000,
  authType: f.authType || 'none',
  username: f.username || '',
  headerName: f.headerName || '',
  confidence: f.confidence || 'Medium',
  severity: f.severity || 'Medium',
  tlp: f.tlp || 'AMBER',
  tags: Array.isArray(f.tags) ? f.tags : [],
  reactivate: Boolean(f.reactivate),
  description: f.description || '',
  cleared: {},
});

const ThreatFeeds = ({ readOnly = false }) => {
  const [feeds, setFeeds] = useState([]);
  const [formats, setFormats] = useState(Object.keys(FORMAT_LABELS));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteIndicators, setDeleteIndicators] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.integrations.getFeeds();
      setFeeds(res.data?.feeds || []);
      if (Array.isArray(res.data?.formats) && res.data.formats.length) setFormats(res.data.formats);
    } catch (err) {
      setError(serverMessage(err, 'Failed to load threat intelligence feeds'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (message) => {
    setNotice(message);
    setTimeout(() => setNotice(null), 5000);
  };

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError(null);
    setTestResult(null);
    setDialogOpen(true);
  };

  const openEdit = (feed) => {
    setEditing(feed);
    setForm(formFromFeed(feed));
    setFormError(null);
    setTestResult(null);
    setDialogOpen(true);
  };

  const buildPayload = () => {
    const payload = {
      name: form.name.trim(),
      url: form.url.trim(),
      format: form.format,
      enabled: form.enabled,
      intervalHours: Number(form.intervalHours) || 6,
      maxIndicators: Number(form.maxIndicators) || 5000,
      authType: form.authType,
      username: form.authType === 'basic' ? form.username.trim() : '',
      headerName: form.authType === 'header' ? form.headerName.trim() : '',
      confidence: form.confidence,
      severity: form.severity,
      tlp: form.tlp,
      tags: form.tags,
      reactivate: form.reactivate,
      description: form.description.trim(),
    };
    ['apiKey', 'apiToken', 'clientSecret'].forEach((key) => {
      if (form.cleared[key]) { if (editing) payload[key] = null; return; }
      if (String(form[key] || '').trim()) payload[key] = String(form[key]).trim();
    });
    return payload;
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.url.trim()) {
      setFormError('Name and URL are required');
      return;
    }
    try {
      setSaving(true);
      setFormError(null);
      const payload = buildPayload();
      if (editing) await apiService.integrations.updateFeed(editing._id, payload);
      else await apiService.integrations.createFeed(payload);
      setDialogOpen(false);
      setEditing(null);
      flash(editing ? 'Feed updated' : 'Feed created');
      await load();
    } catch (err) {
      setFormError(serverMessage(err, 'Failed to save feed'));
    } finally {
      setSaving(false);
    }
  };

  const runTest = async (payload, onDone) => {
    try {
      setTesting(true);
      setTestResult(null);
      const res = await apiService.integrations.testFeed(payload);
      setTestResult({ success: res.data?.success !== false, ...res.data });
    } catch (err) {
      setTestResult({ success: false, error: serverMessage(err, 'Feed test failed') });
    } finally {
      setTesting(false);
      if (onDone) onDone();
    }
  };

  const handleDialogTest = () => {
    if (!form.url.trim()) {
      setFormError('Enter a feed URL before testing');
      return;
    }
    setFormError(null);
    const payload = buildPayload();
    // Blank values must not fail validation (name is optional for a test) or overwrite stored secrets.
    Object.keys(payload).forEach((k) => { if (payload[k] === null || payload[k] === '') delete payload[k]; });
    runTest(editing ? { id: editing._id, ...payload } : payload);
  };

  const handleRowTest = async (feed) => {
    setBusyId(feed._id);
    setEditing(feed);
    setForm(formFromFeed(feed));
    setDialogOpen(true);
    await runTest({ id: feed._id }, () => setBusyId(null));
  };

  const handleToggle = async (feed, enabled) => {
    try {
      setBusyId(feed._id);
      setError(null);
      await apiService.integrations.updateFeed(feed._id, { enabled });
      await load();
    } catch (err) {
      setError(serverMessage(err, 'Failed to update feed'));
    } finally {
      setBusyId(null);
    }
  };

  const handleSync = async (feed) => {
    try {
      setBusyId(feed._id);
      setError(null);
      const res = await apiService.integrations.syncFeed(feed._id);
      const d = res.data || {};
      if (d.error) setError(`${feed.name}: ${d.error}`);
      else flash(`${feed.name}: ${d.created || 0} created / ${d.updated || 0} updated / ${d.skipped || 0} skipped (${d.fetched || 0} fetched${d.format ? `, ${d.format}` : ''})`);
    } catch (err) {
      setError(serverMessage(err, `Failed to sync ${feed.name}`));
    } finally {
      setBusyId(null);
      load();
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      const res = await apiService.integrations.deleteFeed(deleteTarget._id, deleteIndicators);
      const deactivated = res.data?.deactivated || 0;
      setDeleteTarget(null);
      setDeleteIndicators(false);
      flash(`Feed deleted${deactivated ? ` · ${deactivated} indicator${deactivated === 1 ? '' : 's'} deactivated` : ''}`);
      await load();
    } catch (err) {
      setError(serverMessage(err, 'Failed to delete feed'));
    } finally {
      setDeleting(false);
    }
  };

  const secretHelper = (key, hasFlag, hint) => {
    if (form.cleared[key]) return 'Will be cleared when you save.';
    if (editing && hasFlag) return 'Configured — leave blank to keep the existing value.';
    return hint || '';
  };

  const renderSecretField = (key, label, hasFlag, hint) => (
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        type="password"
        label={label}
        value={form[key]}
        onChange={(e) => setField(key, e.target.value)}
        helperText={secretHelper(key, hasFlag, hint)}
        disabled={readOnly || Boolean(form.cleared[key])}
      />
      {editing && hasFlag && (
        <Button
          size="small"
          color={form.cleared[key] ? 'primary' : 'error'}
          onClick={() => setForm((prev) => ({ ...prev, [key]: '', cleared: { ...prev.cleared, [key]: !prev.cleared[key] } }))}
          disabled={readOnly}
          sx={{ mt: 0.5 }}
        >
          {form.cleared[key] ? 'Undo clear' : 'Clear'}
        </Button>
      )}
    </Grid>
  );

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}

      <Card>
        <CardHeader
          avatar={<FeedIcon />}
          title="Threat intelligence feeds"
          subheader="Import indicators from public or commercial feeds on a schedule"
          action={
            <Box display="flex" gap={1}>
              <Tooltip title="Reload">
                <span>
                  <IconButton size="small" onClick={load} disabled={loading}><RefreshIcon /></IconButton>
                </span>
              </Tooltip>
              <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openAdd} disabled={readOnly}>
                Add feed
              </Button>
            </Box>
          }
        />
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
          ) : feeds.length === 0 ? (
            <Alert severity="info">
              <AlertTitle>No feeds configured</AlertTitle>
              Add a feed URL to import indicators automatically. Well-known public feeds you can start with:
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
                <li>Feodo Tracker IP blocklist — https://feodotracker.abuse.ch/downloads/ipblocklist.txt</li>
                <li>URLhaus URL list — https://urlhaus.abuse.ch/downloads/text/</li>
                <li>OpenPhish — https://openphish.com/feed.txt</li>
              </Box>
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Format</TableCell>
                    <TableCell>URL</TableCell>
                    <TableCell>Interval</TableCell>
                    <TableCell>Enabled</TableCell>
                    <TableCell>Last sync</TableCell>
                    <TableCell>Imported</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {feeds.map((f) => {
                    const sync = f.lastSync || {};
                    const stats = f.stats || {};
                    return (
                      <TableRow key={f._id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{f.name}</Typography>
                          {f.description && (
                            <Typography variant="caption" color="text.secondary">{truncate(f.description, 60)}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" variant="outlined" label={FORMAT_LABELS[f.format] || f.format || 'auto'} />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={f.url || ''}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{truncate(f.url)}</Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">every {f.intervalHours || 6} h</Typography>
                        </TableCell>
                        <TableCell>
                          <Switch
                            size="small"
                            checked={f.enabled !== false}
                            onChange={(e) => handleToggle(f, e.target.checked)}
                            disabled={readOnly || busyId === f._id}
                          />
                        </TableCell>
                        <TableCell>
                          {sync.at ? (
                            <>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <Chip size="small" color={sync.success ? 'success' : 'error'} label={sync.success ? 'Success' : 'Failed'} />
                                <Typography variant="caption">{formatRelativeTime(sync.at)}</Typography>
                              </Box>
                              {sync.success ? (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {sync.created || 0} created / {sync.updated || 0} updated / {sync.skipped || 0} skipped
                                </Typography>
                              ) : (
                                <Tooltip title={sync.error || ''}>
                                  <Typography variant="caption" color="error.main" display="block">
                                    {truncate(sync.error || 'Sync failed', 40)}
                                  </Typography>
                                </Tooltip>
                              )}
                            </>
                          ) : (
                            <Typography variant="caption" color="text.secondary">Never</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{stats.indicators || 0}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {stats.syncs || 0} sync{(stats.syncs || 0) === 1 ? '' : 's'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Sync now">
                            <span>
                              <IconButton size="small" onClick={() => handleSync(f)} disabled={readOnly || busyId === f._id}>
                                {busyId === f._id ? <CircularProgress size={16} /> : <SyncIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Test fetch">
                            <span>
                              <IconButton size="small" onClick={() => handleRowTest(f)} disabled={readOnly || busyId === f._id}>
                                <TestIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(f)}><EditIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <span>
                              <IconButton size="small" color="error" onClick={() => { setDeleteTarget(f); setDeleteIndicators(false); }} disabled={readOnly}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add / edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? `Edit ${editing.name}` : 'Add threat intelligence feed'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {formError && <Grid item xs={12}><Alert severity="error">{formError}</Alert></Grid>}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Name"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                placeholder="Feodo Tracker"
                disabled={readOnly}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={readOnly}>
                <InputLabel>Format</InputLabel>
                <Select value={form.format} label="Format" onChange={(e) => setField('format', e.target.value)}>
                  {formats.map((key) => (
                    <MenuItem key={key} value={key}>{FORMAT_LABELS[key] || key}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Feed URL"
                value={form.url}
                onChange={(e) => setField('url', e.target.value)}
                placeholder="https://feodotracker.abuse.ch/downloads/ipblocklist.txt"
                helperText="Public examples: https://feodotracker.abuse.ch/downloads/ipblocklist.txt · https://urlhaus.abuse.ch/downloads/text/ · https://openphish.com/feed.txt"
                disabled={readOnly}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Interval (hours)"
                value={form.intervalHours}
                onChange={(e) => setField('intervalHours', e.target.value)}
                inputProps={{ min: 1, max: 168 }}
                helperText="1–168, default 6"
                disabled={readOnly}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Max indicators per sync"
                value={form.maxIndicators}
                onChange={(e) => setField('maxIndicators', e.target.value)}
                inputProps={{ min: 1, max: 50000 }}
                helperText="Default 5000"
                disabled={readOnly}
              />
            </Grid>
            <Grid item xs={12} sm={4} display="flex" alignItems="center">
              <FormControlLabel
                control={<Switch checked={form.enabled} onChange={(e) => setField('enabled', e.target.checked)} disabled={readOnly} />}
                label="Enabled"
              />
            </Grid>

            <Grid item xs={12}><Divider /></Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={readOnly}>
                <InputLabel>Authentication</InputLabel>
                <Select value={form.authType} label="Authentication" onChange={(e) => setField('authType', e.target.value)}>
                  {Object.entries(AUTH_LABELS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {form.authType === 'basic' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Username"
                    value={form.username}
                    onChange={(e) => setField('username', e.target.value)}
                    disabled={readOnly}
                  />
                </Grid>
                {renderSecretField('clientSecret', 'Password', editing?.hasClientSecret)}
              </>
            )}
            {form.authType === 'bearer' && renderSecretField('apiToken', 'Bearer token', editing?.hasApiToken)}
            {form.authType === 'header' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Header name"
                    value={form.headerName}
                    onChange={(e) => setField('headerName', e.target.value)}
                    placeholder="X-API-Key"
                    disabled={readOnly}
                  />
                </Grid>
                {renderSecretField('apiKey', 'Header value', editing?.hasApiKey)}
              </>
            )}

            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Defaults for imported indicators</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth disabled={readOnly}>
                <InputLabel>Confidence</InputLabel>
                <Select value={form.confidence} label="Confidence" onChange={(e) => setField('confidence', e.target.value)}>
                  {['Low', 'Medium', 'High'].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth disabled={readOnly}>
                <InputLabel>Severity</InputLabel>
                <Select value={form.severity} label="Severity" onChange={(e) => setField('severity', e.target.value)}>
                  {['Low', 'Medium', 'High', 'Critical'].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth disabled={readOnly}>
                <InputLabel>TLP</InputLabel>
                <Select value={form.tlp} label="TLP" onChange={(e) => setField('tlp', e.target.value)}>
                  {['WHITE', 'GREEN', 'AMBER', 'RED'].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={form.tags}
                onChange={(e, value) => setField('tags', value.slice(0, 10))}
                disabled={readOnly}
                renderInput={(params) => (
                  <TextField {...params} label="Tags" placeholder="Press Enter to add" helperText="Applied to every indicator imported from this feed (max 10)." />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.reactivate} onChange={(e) => setField('reactivate', e.target.checked)} disabled={readOnly} />}
                label="Re-activate indicators that were marked inactive"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Description"
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
                disabled={readOnly}
              />
            </Grid>

            {testResult && (
              <Grid item xs={12}>
                <Alert severity={testResult.success ? 'success' : 'error'} onClose={() => setTestResult(null)}>
                  <AlertTitle>{testResult.success ? 'Feed reachable' : 'Feed test failed'}</AlertTitle>
                  {testResult.success ? (
                    <>
                      <Typography variant="body2">
                        Detected format <strong>{testResult.format || 'unknown'}</strong> · {testResult.fetched || 0} entries fetched ·{' '}
                        {testResult.usable || 0} usable indicators
                      </Typography>
                      {testResult.byType && Object.keys(testResult.byType).length > 0 && (
                        <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                          {Object.entries(testResult.byType).map(([type, count]) => (
                            <Chip key={type} size="small" label={`${type}: ${count}`} />
                          ))}
                        </Box>
                      )}
                      {Array.isArray(testResult.sample) && testResult.sample.length > 0 && (
                        <Box component="pre" sx={{ mt: 1, mb: 0, fontSize: 12, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                          {testResult.sample.map((s) => `${s.type}  ${s.value}`).join('\n')}
                        </Box>
                      )}
                    </>
                  ) : (
                    testResult.error || testResult.message || 'Could not fetch or parse the feed'
                  )}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDialogTest}
            disabled={testing || readOnly}
            startIcon={testing ? <CircularProgress size={16} /> : <TestIcon />}
          >
            Test feed
          </Button>
          <Box flexGrow={1} />
          <Button onClick={() => setDialogOpen(false)}>{readOnly ? 'Close' : 'Cancel'}</Button>
          {!readOnly && (
            <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : null}>
              {editing ? 'Save changes' : 'Add feed'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete feed</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{deleteTarget?.name}</strong>? Scheduled imports stop immediately.
          </DialogContentText>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Checkbox checked={deleteIndicators} onChange={(e) => setDeleteIndicators(e.target.checked)} />}
            label="Also deactivate indicators imported by this feed"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ThreatFeeds;
